/**
 * Presentation arithmetic for the slot matrix.
 *
 * Kept out of the component because this repo's vitest is node-only and cannot
 * run `.tsx` — sizing thresholds and occupancy maths left inside `SlotMap`
 * would have no coverage at all.
 */

import { isAtOrUnder, isOccupiable } from './storageTree'
import type { ApiStorageNode, StorageCapacity } from '@/types/storage'

/** How much room each cell gets. Chosen from the grid's size, not its kind. */
export type CellSize = 'lg' | 'md' | 'sm'

/**
 * A 2×5 of racks should look like ten real shelves; a 9×9 cryobox at that size
 * is a wall of buttons nobody can scan. Thresholds are on the total count
 * rather than rows or columns, since 1×81 is exactly as dense as 9×9.
 */
export function cellSizeFor(count: number): CellSize {
  if (count <= 25) return 'lg'
  if (count <= 64) return 'md'
  return 'sm'
}

/**
 * The smallest cells cannot hold a legible code — it moves to the hover card.
 *
 * A band-level answer, and therefore only a fallback: the band is chosen from
 * the CELL COUNT, which says nothing about how much width the grid was actually
 * given. `showsCodeAt` is the real decision — see below.
 */
export function showsCode(size: CellSize): boolean {
  return size !== 'sm'
}

/**
 * Narrowest a cell can be and still say its whole code, per band, in px.
 *
 * `truncate` means a too-narrow cell prints a fragment and an ellipsis, which is
 * worse than printing nothing: a partial code invites misreading a location, and
 * a location read wrong is a vial put back in the wrong place.
 *
 * MEASURED, not estimated — in Chromium, against the app's own font stack, at
 * each band's font size, for the widest code the label schemes can mint
 * (`R2C11`, five characters), plus the cell's `px-1` and its border:
 *
 *   lg  14px type → 40.8px of text → 51px
 *   md  12px type → 35.6px of text → 46px
 *   sm  10px type → 30.3px of text → 41px
 *
 * One row per band because the bands do not share a font size, and a single
 * threshold would either truncate `lg` or silence `sm` needlessly. Re-measure
 * these if the `text` classes in `SlotMap`'s `SIZE_STYLES` change — the two
 * tables describe the same three cells from different sides.
 */
export const CODE_FITS_FROM_PX: Record<CellSize, number> = { lg: 51, md: 46, sm: 41 }

/** Whether a cell of this band, at this measured width, should print its code. */
export function showsCodeAt(size: CellSize, cellWidthPx: number): boolean {
  return cellWidthPx >= CODE_FITS_FROM_PX[size]
}

/**
 * The width one column track will actually be given.
 *
 * Mirrors what the browser does to `minmax(min, max)` tracks in a grid that
 * fills its container: every column grows equally from its floor until either
 * the space runs out or the ceiling stops it. Recomputing it here — rather than
 * measuring a rendered cell — is what lets the code/no-code decision be made in
 * the same pass that lays the grid out, with no second render.
 *
 * `containerPx` of 0 (an unmeasured or hidden grid) yields the floor, so the
 * caller falls back to the band heuristic rather than to a nonsense width.
 */
export function cellWidthPx({
  containerPx,
  columns,
  gutterPx,
  gapPx,
  minPx,
  maxPx,
}: {
  /** Content width available to the whole matrix, gutter included. */
  containerPx: number
  columns: number
  /** The row-label gutter, which is a fixed track. */
  gutterPx: number
  gapPx: number
  minPx: number
  maxPx: number
}): number {
  if (columns < 1) return 0
  // One gap sits between the gutter and the first cell, then one between each
  // pair of cells: as many gaps as there are cell columns.
  const free = containerPx - gutterPx - gapPx * columns
  return Math.max(minPx, Math.min(maxPx, free / columns))
}

/**
 * The narrowest a cell may shrink to so the whole row still fits its card —
 * the smallest band's floor. A cell narrower than its band's own floor just
 * stops printing its code (`showsCodeAt`); it never pushes the last column
 * out of sight.
 */
export const FIT_FLOOR_PX = 28

/**
 * The width range of the whole matrix, gutter and gaps included.
 *
 * The map lays its columns out as `minmax(0, 1fr)` in a full-width grid
 * capped at `maxPx` (cells stop growing at their ceiling) and floored at
 * `minPx`. Only a card narrower than `minPx` scrolls — sideways, inside the
 * card, with the column numbers scrolling too.
 */
export function mapWidthBounds({
  columns,
  gutterPx,
  gapPx,
  floorPx,
  maxPx,
}: {
  columns: number
  gutterPx: number
  gapPx: number
  floorPx: number
  maxPx: number
}): { minPx: number; maxPx: number } {
  const fixed = gutterPx + gapPx * columns
  return { minPx: fixed + columns * floorPx, maxPx: fixed + columns * maxPx }
}

/**
 * Beyond this, a grid stays at its natural density even when it holds stock.
 *
 * A 12×12 is the point where growing every cell costs more scrolling than the
 * labels are worth; past it, what is where is a question for search and the
 * hover card, not for reading a wall of codes.
 */
const CONTENTS_LEGIBLE_UP_TO = 144

/**
 * The size a grid renders at, given whether anything is actually stored in it.
 *
 * A 9×9 cryobox — the commonest thing in this app — lands in the smallest band,
 * whose cells may be too narrow to say anything at all. That is right for an
 * empty box and wrong for a full one: the whole reason to look at a full box is
 * to see WHAT is in it, and hovering 81 cells one at a time is not an answer. So
 * a grid holding stock is given the room to say so.
 *
 * This raises the band, which raises the cell's floor AND its ceiling; whether a
 * code is actually printed is then `showsCodeAt` on the measured width. A box
 * squeezed into a narrow column still ends up wordless, and correctly so.
 */
export function gridSizeFor(count: number, hasContents: boolean): CellSize {
  const natural = cellSizeFor(count)
  if (!hasContents || natural !== 'sm' || count > CONTENTS_LEGIBLE_UP_TO) return natural
  return 'md'
}

export interface ContainerFill {
  /** Real slots beneath this container (holding areas never count). */
  total: number
  blocked: number
  /** Slots something could actually go into. */
  usable: number
  used: number
  /** Percentage of usable slots in use, 0–100. */
  pct: number
  /** What to print on the cell: "empty", "FULL", "62%", or "no slots". */
  label: string
}

/**
 * How full one container is, counted from the slots beneath it.
 *
 * This is what lets a freezer's rack grid answer "where is there room" without
 * opening all ten racks. Containment is checked with `isAtOrUnder` rather than
 * a raw prefix match, or rack R1 would swallow every slot in R10.
 *
 * Percentage is of USABLE slots, not declared ones, so a rack whose only free
 * shelf is blocked reads as full — which it is.
 */
export function containerFill(
  nodes: ApiStorageNode[],
  container: ApiStorageNode,
  occupiedIds: ReadonlySet<string>,
): ContainerFill {
  // NOT called by the UI any more. Asking this per cell is what made opening a
  // rack slow: a hundred boxes each rescanned the whole freezer, 276 ms of
  // arithmetic per render on a million-slot unit and again on every hover. Both
  // the hierarchy and the slot map read the server's figure through
  // `fillFromCapacity` instead.
  //
  // It stays because it is the independent statement of the counting rule that
  // `containerFillIndex` is checked against — one scan versus one traversal,
  // asserted to agree. Delete it and the index has only itself to agree with.
  //
  // Scoped to this container, then counted by the same rule the index uses.
  // Both have to honour grid declarations or they disagree about how full a box
  // is — and a box reading "3 of 3" on one screen and "3 of 100" on another is
  // worse than either being wrong alone.
  const scope = nodes.filter((n) => isAtOrUnder(n.path, container.path))
  const index = containerFillIndex(scope, occupiedIds)
  return index.get(container.id) ?? fillFrom({ total: 0, blocked: 0, used: 0 })
}

/** The derived half of a `ContainerFill`, from counts however they were gathered. */
function fillFrom({
  total,
  blocked,
  used,
}: {
  total: number
  blocked: number
  used: number
}): ContainerFill {
  const usable = total - blocked
  const pct = usable === 0 ? 0 : Math.round((used / usable) * 100)

  let label: string
  if (total === 0) label = 'no slots'
  else if (usable === 0) label = 'FULL'
  else if (used === 0) label = 'empty'
  else if (used >= usable) label = 'FULL'
  else label = `${pct}%`

  return { total, blocked, usable, used, pct, label }
}

/**
 * Every container's fill, in ONE pass over the tree.
 *
 * `containerFill` scans the whole node array per container, which is fine for a
 * single cell and quadratic for a panel: a freezer built at the new layout
 * ceiling has tens of thousands of positions, and the hierarchy re-scanned all
 * of them for every visible row, on every render. Here each slot walks up its
 * own ancestor paths instead — bounded by tree DEPTH, not by node count — so the
 * whole panel costs one traversal.
 *
 * Same counting rules as `containerFill`, and the same derivation, so a row read
 * from this index and a cell read from that function can never disagree.
 */
export type FillIndex = ReadonlyMap<string, ContainerFill>

/**
 * The server's capacity for a container, in the shape a cell prints.
 *
 * The hierarchy pane and the slot map read each container's figures off the
 * tree response now instead of adding up slots in the browser — which is what
 * lets the response leave the positions out entirely, taking a large freezer
 * from a 29 MB payload to under one.
 *
 * The two vocabularies describe the same numbers: the server says `declared`
 * and `spaces`, a cell prints `total` and `usable`. Converting here, through
 * the same `fillFrom` every locally counted fill goes through, is what stops a
 * bar read from the server and one counted on the client from ever disagreeing
 * about the label.
 */
export function fillFromCapacity(capacity: StorageCapacity | undefined): ContainerFill {
  if (!capacity) return EMPTY_FILL
  return fillFrom({
    total: capacity.declared,
    blocked: capacity.blocked,
    used: capacity.used,
  })
}

/** A node the index has never seen holds nothing, which is still an answer. */
export function fillOf(fills: FillIndex, nodeId: string): ContainerFill {
  return fills.get(nodeId) ?? EMPTY_FILL
}

const EMPTY_FILL: ContainerFill = {
  total: 0,
  blocked: 0,
  usable: 0,
  used: 0,
  pct: 0,
  label: 'no slots',
}

export function containerFillIndex(
  nodes: ApiStorageNode[],
  occupiedIds: ReadonlySet<string>,
): FillIndex {
  const idByPath = new Map(nodes.map((n) => [n.path, n.id]))
  const counts = new Map<string, { total: number; blocked: number; used: number }>()

  // Every node that declares a grid of positions, so a stored cell can be
  // recognised as one the declaration already counted.
  const declaringById = new Map<string, ApiStorageNode>()
  for (const node of nodes) {
    if (node.gridChildKind === 'POSITION' && node.gridRows != null && node.gridColumns != null) {
      declaringById.set(node.id, node)
    }
  }

  /**
   * Credit a count to a node and to every ancestor above it.
   *
   * Ancestor PATHS, not a prefix match: splitting on the separator is what
   * keeps R10's slots out of R1's count, the same boundary `isAtOrUnder` draws.
   * `self` is true for a declaration, which belongs to the declaring node as
   * well as to everything above it — a box's own bar counts its own slots.
   */
  const credit = (
    path: string,
    add: { total: number; blocked: number; used: number },
    self: boolean,
  ) => {
    const segments = path.split('/')
    const from = self ? segments.length : segments.length - 1
    for (let i = 1; i <= from; i += 1) {
      const ancestorId = idByPath.get(segments.slice(0, i).join('/'))
      if (!ancestorId) continue
      const bucket = counts.get(ancestorId) ?? { total: 0, blocked: 0, used: 0 }
      bucket.total += add.total
      bucket.blocked += add.blocked
      bucket.used += add.used
      counts.set(ancestorId, bucket)
    }
  }

  // Slots a container only DECLARES. A box's grid describes its hundred cells
  // whether or not any of them has a row, so counting rows alone would make a
  // box holding three lots read "3 of 3 — FULL" when it is three of a hundred.
  for (const parent of declaringById.values()) {
    credit(
      parent.path,
      { total: (parent.gridRows as number) * (parent.gridColumns as number), blocked: 0, used: 0 },
      true,
    )
  }

  for (const node of nodes) {
    if (!isOccupiable(node.kind) || node.isHoldingArea) continue

    // Already counted by its parent's declaration? Then contribute only its
    // state, never another unit of total. Coordinates must fall INSIDE the
    // declared grid — a grid that was later shrunk leaves real slots outside it.
    const parent = node.parentId ? declaringById.get(node.parentId) : undefined
    const declaredAlready =
      parent != null &&
      node.gridRow != null &&
      node.gridColumn != null &&
      node.gridRow <= (parent.gridRows as number) &&
      node.gridColumn <= (parent.gridColumns as number)

    credit(
      node.path,
      {
        total: declaredAlready ? 0 : 1,
        blocked: node.blocked ? 1 : 0,
        used: !node.blocked && occupiedIds.has(node.id) ? 1 : 0,
      },
      false,
    )
  }

  // Containers with nothing beneath them still need an answer ("no slots").
  const out = new Map<string, ContainerFill>()
  for (const node of nodes) {
    out.set(node.id, fillFrom(counts.get(node.id) ?? { total: 0, blocked: 0, used: 0 }))
  }
  return out
}

/** Most cells a preview will draw before it starts costing more than it explains. */
export const MAX_PREVIEW_CELLS = 400

export interface PreviewExtent {
  rowsShown: number
  truncated: boolean
  hiddenRows: number
}

/**
 * How much of a declared matrix to draw in the preview.
 *
 * Whole grids are shown wherever possible — seeing the real shape is the point
 * of the preview, and a 9×9 is only 81 cells. A 100×50, though, is 5000 nodes
 * mounted while someone is still typing in the rows field, so the preview is
 * capped by rows and says how many it left out. At least one row always
 * renders, however wide, so the shape is never invisible.
 */
export function previewRows(rows: number, columns: number): PreviewExtent {
  if (!Number.isInteger(rows) || !Number.isInteger(columns) || rows < 1 || columns < 1) {
    return { rowsShown: 0, truncated: false, hiddenRows: 0 }
  }
  if (rows * columns <= MAX_PREVIEW_CELLS) {
    return { rowsShown: rows, truncated: false, hiddenRows: 0 }
  }
  const rowsShown = Math.max(1, Math.floor(MAX_PREVIEW_CELLS / columns))
  return {
    rowsShown: Math.min(rows, rowsShown),
    truncated: rowsShown < rows,
    hiddenRows: Math.max(0, rows - rowsShown),
  }
}

export type FillTone = 'empty' | 'some' | 'most' | 'full'

/** Coarse bands so the meter reads at a glance instead of by its number. */
export function fillTone(pct: number): FillTone {
  if (pct <= 0) return 'empty'
  if (pct >= 100) return 'full'
  return pct >= 80 ? 'most' : 'some'
}
